import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { Modal } from '../../../../core/design-system/components/Modal';
import { Input } from '../../../../core/design-system/components/Input';
import { Button } from '../../../../core/design-system/components/Button';
import { Text } from '../../../../core/design-system/components/Text';
import { Toast } from '../../../../core/design-system/components/Toast';
import { usePhoneVerification } from '../../../../core/hooks/usePhoneVerification';
import './styles.css';

export default function PhoneVerificationModal({ isOpen, onClose, onVerified }) {
  const { t, i18n } = useTranslation();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const {
    sendVerificationCode,
    verifyCode,
    isLoading,
    isVerifying,
    error,
    verificationSent,
    cooldownRemaining,
    resetVerification
  } = usePhoneVerification();

  useEffect(() => {
    if (isOpen) {
      setPhone('');
      setCode('');
      resetVerification();
    }
  }, [isOpen, resetVerification]);

  const formatTimeRemaining = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return {
      minutes,
      seconds
    };
  };

  const getDefaultCountry = () => {
    const language = i18n.language || 'fr';
    const languageToCountry = {
      fr: 'FR',
      en: 'US',
      es: 'ES',
      de: 'DE',
      it: 'IT'
    };
    return languageToCountry[language.split('-')[0]] || 'FR';
  };

  const validatePhoneNumber = (phoneNumber) => {
    try {
      if (!phoneNumber.startsWith('+')) {
        return isValidPhoneNumber(phoneNumber, getDefaultCountry());
      }
      return isValidPhoneNumber(phoneNumber);
    } catch {
      return false;
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    try {
      const defaultCountry = getDefaultCountry();
      const parsedNumber = phoneNumber.startsWith('+')
        ? parsePhoneNumberFromString(phoneNumber)
        : parsePhoneNumberFromString(phoneNumber, defaultCountry);
      
      return parsedNumber ? parsedNumber.format('E.164') : phoneNumber;
    } catch {
      return phoneNumber;
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
  };

  const handleSendCode = async () => {
    const isValid = validatePhoneNumber(phone);
    if (!isValid) {
      setShowToast(true);
      setToastMessage(t('settings:phone.invalidNumber'));
      setToastType('error');
      return;
    }

    const formatted = formatPhoneNumber(phone);
    setPhone(formatted);

    try {
      await sendVerificationCode(formatted);
      setShowToast(true);
      setToastMessage(t('settings:phone.codeSent', { phone: formatted }));
      setToastType('success');
    } catch (err) {
      setShowToast(true);
      setToastMessage(err.message);
      setToastType('error');
    }
  };

  const handleVerifyCode = async () => {
    try {
      const result = await verifyCode(code);
      if (result.success) {
        setShowToast(true);
        setToastMessage(t('settings:phone.verificationSuccess'));
        setToastType('success');
        onVerified({
          phone: formatPhoneNumber(phone),
          phoneVerified: true
        });
        handleClose();
      }
    } catch (err) {
      setShowToast(true);
      setToastMessage(err.message);
      setToastType('error');
    }
  };

  const handleClose = () => {
    setPhone('');
    setCode('');
    resetVerification();
    onClose();
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={handleClose}
        title={t('settings:phone.verificationTitle')}
        className="phone-verification-modal"
      >
        <div className="phone-verification-content">
          {!verificationSent ? (
            <>
              <Text variant="body" className="verification-description">
                {t('settings:phone.verificationDescription')}
              </Text>
              <Input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder={t('settings:phone.placeholder')}
                error={error}
                disabled={isLoading}
                className="phone-input"
              />
              <Text variant="caption" className="phone-format-hint">
                {t('settings:phone.formatHint')}
              </Text>
            </>
          ) : (
            <>
              <Text variant="body" className="code-sent-message">
                {t('settings:phone.codeSent', { phone })}
              </Text>
              <Input
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder={t('settings:phone.codePlaceholder')}
                error={error}
                disabled={isVerifying}
                className="code-input"
                maxLength={6}
              />
              {cooldownRemaining > 0 && (
                <Text variant="caption" className="resend-countdown">
                  {t('settings:phone.resendCountdown', formatTimeRemaining(cooldownRemaining))}
                </Text>
              )}
            </>
          )}
          
          <div className="verification-actions">
            <Button 
              variant="secondary" 
              onClick={handleClose}
              className="cancel-button"
              disabled={isLoading || isVerifying}
            >
              {t('settings:common.cancel')}
            </Button>
            {!verificationSent ? (
              <Button
                variant="primary"
                onClick={handleSendCode}
                disabled={!phone || phone.length < 6 || isLoading}
                className="action-button"
              >
                {isLoading ? t('common:loading') : t('settings:phone.sendCode')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleVerifyCode}
                disabled={!code || isVerifying}
                className="action-button"
              >
                {isVerifying ? t('common:loading') : t('settings:phone.verify')}
              </Button>
            )}
          </div>
        </div>
      </Modal>
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
          duration={5000}
        />
      )}
    </>
  );
} 